/**
 * Turns an API failure into something a partner can read (NFR-6, §7).
 *
 * The server answers with a stable `error.code`; the client owns the wording.
 * That split is what lets every message exist in Bangla and English without
 * the API carrying translations, and it means a partner never sees a raw
 * `SELF_APPROVAL_FORBIDDEN` on screen.
 */

import { ERROR_CODE, type ErrorCode } from '@agrofarm/shared';

interface ApiErrorBody {
  success: false;
  error: {
    code: ErrorCode;
    message: string;
    fields?: Record<string, string>;
    requestId?: string;
  };
}

interface AxiosLikeError {
  response?: { status?: number; data?: unknown };
  code?: string;
}

const isApiErrorBody = (data: unknown): data is ApiErrorBody =>
  typeof data === 'object' &&
  data !== null &&
  'error' in data &&
  typeof (data as ApiErrorBody).error?.code === 'string';

/** The i18n key for an error, so the caller translates in the partner's language. */
export function errorMessageKey(error: unknown): string {
  const candidate = error as AxiosLikeError | undefined;
  const body = candidate?.response?.data;

  if (isApiErrorBody(body)) return `error.${body.error.code}`;

  // No response at all: the request never reached the farm's server. Common on
  // a rural connection, and a different problem from anything the API said.
  if (candidate?.response === undefined) return 'error.network';

  return `error.${ERROR_CODE.INTERNAL_ERROR}`;
}

/** Per-field messages for inline form errors, when the server sent them. */
export function errorFields(error: unknown): Record<string, string> | undefined {
  const body = (error as AxiosLikeError | undefined)?.response?.data;
  return isApiErrorBody(body) ? body.error.fields : undefined;
}

/** The support reference shown on a 500, so a partner can quote it. */
export function errorRequestId(error: unknown): string | undefined {
  const body = (error as AxiosLikeError | undefined)?.response?.data;
  return isApiErrorBody(body) ? body.error.requestId : undefined;
}
